import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Tab, Nav } from 'react-bootstrap';
import api from "../../../api/axiosConfig";
import { useAuth } from '../../../context/AuthContext';
import './Settings.css';

const Settings = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('general');

  // General Settings
  const [siteName, setSiteName] = useState('');
  const [siteDescription, setSiteDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');

  // Profile Settings
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Notification Settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [postNotifications, setPostNotifications] = useState(true);
  const [commentNotifications, setCommentNotifications] = useState(false);
  const [newsletter, setNewsletter] = useState(true);

  // Load initial data
  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setEmail(user.email || '');
      loadSiteSettings();
    }
  }, [user]);

  const loadSiteSettings = async () => {
    try {
      const siteId = user?.site_id;
      if (!siteId) return;

      const response = await api.get(`/sites/${siteId}`);
      if (response.data.success) {
        const siteData = response.data.data;
        setSiteName(siteData.name || '');
        setSiteDescription(siteData.description || '');
        setContactEmail(siteData.contact_email || '');
        setPhoneNumber(siteData.phone_number || '');
      }
    } catch (err) {
      console.error('Error loading site settings:', err);
    }
  };

  const saveGeneralSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const siteId = user?.site_id;
      if (!siteId) {
        setError('Site ID not found.');
        return;
      }

      const response = await api.put(`/sites/${siteId}`, {
        name: siteName,
        description: siteDescription,
        contact_email: contactEmail,
        phone_number: phoneNumber,
        updated_by: user.id
      });

      if (response.data.success) {
        setSuccessMessage('General settings updated successfully!');
      } else {
        setError(response.data.message || 'Failed to update settings.');
      }
    } catch (err) {
      console.error('Error updating general settings:', err);
      setError('Failed to update general settings.');
    } finally {
      setLoading(false);
    }
  };

  const saveProfileSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const updateData = {
        first_name: firstName,
        last_name: lastName,
        email: email,
        updated_by: user.id
      };

      if (currentPassword && newPassword) {
        if (newPassword !== confirmPassword) {
          setError('New passwords do not match.');
          setLoading(false);
          return;
        }
        if (newPassword.length < 6) {
          setError('New password must be at least 6 characters long.');
          setLoading(false);
          return;
        }
        updateData.current_password = currentPassword;
        updateData.new_password = newPassword;
      }

      const response = await api.put(`/users/${user.id}`, updateData);

      if (response.data.success) {
        setSuccessMessage('Profile updated successfully!');
        updateUser(response.data.data);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(response.data.message || 'Failed to update profile.');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setLoading(false);
    }
  };

  const saveNotificationSettings = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.put(`/users/${user.id}/preferences`, {
        email_notifications: emailNotifications,
        post_notifications: postNotifications,
        comment_notifications: commentNotifications,
        newsletter: newsletter
      });

      if (response.data.success) {
        setSuccessMessage('Notification settings updated successfully!');
      } else {
        setError(response.data.message || 'Failed to update notification settings.');
      }
    } catch (err) {
      console.error('Error updating notification settings:', err);
      setError('Failed to update notification settings.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setError('');
    setSuccessMessage('');
  };

  return (
    <Container className="sj-settings-container">
      <div className="sj-settings-header">
        <h1 className="sj-settings-title">Settings</h1>
        <p className="sj-settings-subtitle">Manage your website and account preferences</p>
      </div>

      {error && (
        <Alert variant="danger" className="sj-settings-alert">
          <i className="fas fa-exclamation-triangle sj-alert-icon"></i>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" className="sj-settings-alert">
          <i className="fas fa-check-circle sj-alert-icon"></i>
          {successMessage}
        </Alert>
      )}

      <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
        <Row>
          <Col lg={3}>
            <Card className="sj-settings-sidebar">
              <Card.Body className="sj-sidebar-body">
                <Nav variant="pills" className="flex-column sj-settings-nav">
                  <Nav.Item>
                    <Nav.Link eventKey="general" onClick={resetForm} className="sj-nav-item">
                      <div className="sj-nav-icon">
                        <i className="fas fa-cog"></i>
                      </div>
                      <div className="sj-nav-content">
                        <div className="sj-nav-title">General</div>
                        <div className="sj-nav-desc">Site information</div>
                      </div>
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link eventKey="profile" onClick={resetForm} className="sj-nav-item">
                      <div className="sj-nav-icon">
                        <i className="fas fa-user"></i>
                      </div>
                      <div className="sj-nav-content">
                        <div className="sj-nav-title">Profile</div>
                        <div className="sj-nav-desc">Personal information</div>
                      </div>
                    </Nav.Link>
                  </Nav.Item>

                  <Nav.Item>
                    <Nav.Link eventKey="notifications" onClick={resetForm} className="sj-nav-item">
                      <div className="sj-nav-icon">
                        <i className="fas fa-bell"></i>
                      </div>
                      <div className="sj-nav-content">
                        <div className="sj-nav-title">Notifications</div>
                        <div className="sj-nav-desc">Alert preferences</div>
                      </div>
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={9}>
            <Tab.Content>
              {/* General Settings Tab */}
              <Tab.Pane eventKey="general">
                <Card className="sj-settings-card">
                  <Card.Header className="sj-card-header">
                    <div className="sj-header-content">
                      <i className="fas fa-cog sj-header-icon"></i>
                      <div>
                        <h5 className="sj-card-title">General Settings</h5>
                        <p className="sj-card-subtitle">Manage your website information and contact details</p>
                      </div>
                    </div>
                  </Card.Header>

                  <Card.Body className="sj-card-body">
                    <Form onSubmit={saveGeneralSettings} className="sj-settings-form">
                      <Row>
                        <Col md={6}>
                          <Form.Group className="sj-form-group">
                            <Form.Label className="sj-form-label">Site Name *</Form.Label>
                            <Form.Control
                              type="text"
                              value={siteName}
                              onChange={(e) => setSiteName(e.target.value)}
                              required
                              placeholder="Enter your site name"
                              className="sj-form-control"
                            />
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group className="sj-form-group">
                            <Form.Label className="sj-form-label">Contact Email</Form.Label>
                            <Form.Control
                              type="email"
                              value={contactEmail}
                              onChange={(e) => setContactEmail(e.target.value)}
                              placeholder="contact@example.com"
                              className="sj-form-control"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="sj-form-group">
                        <Form.Label className="sj-form-label">Site Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={4}
                          value={siteDescription}
                          onChange={(e) => setSiteDescription(e.target.value)}
                          placeholder="Brief description of your website..."
                          className="sj-form-control"
                        />
                        <Form.Text className="sj-form-help">
                          This description may be used in search results and social media.
                        </Form.Text>
                      </Form.Group>

                      <Form.Group className="sj-form-group">
                        <Form.Label className="sj-form-label">Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          placeholder="+1 (555) 123-4567"
                          className="sj-form-control"
                        />
                      </Form.Group>

                      <div className="sj-settings-actions">
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading}
                          className="sj-save-btn"
                        >
                          {loading ? (
                            <>
                              <i className="fas fa-spinner fa-spin sj-btn-icon"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save sj-btn-icon"></i>
                              Save General Settings
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Profile Settings Tab */}
              <Tab.Pane eventKey="profile">
                <Card className="sj-settings-card">
                  <Card.Header className="sj-card-header">
                    <div className="sj-header-content">
                      <i className="fas fa-user sj-header-icon"></i>
                      <div>
                        <h5 className="sj-card-title">Profile Settings</h5>
                        <p className="sj-card-subtitle">Update your personal information and password</p>
                      </div>
                    </div>
                  </Card.Header>

                  <Card.Body className="sj-card-body">
                    <Form onSubmit={saveProfileSettings} className="sj-settings-form">
                      <div className="sj-section-title">Personal Information</div>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="sj-form-group">
                            <Form.Label className="sj-form-label">First Name *</Form.Label>
                            <Form.Control
                              type="text"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              required
                              className="sj-form-control"
                            />
                          </Form.Group>
                        </Col>

                        <Col md={6}>
                          <Form.Group className="sj-form-group">
                            <Form.Label className="sj-form-label">Last Name *</Form.Label>
                            <Form.Control
                              type="text"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              required
                              className="sj-form-control"
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="sj-form-group">
                        <Form.Label className="sj-form-label">Email Address *</Form.Label>
                        <Form.Control
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          className="sj-form-control"
                        />
                      </Form.Group>

                      <div className="sj-section-divider">
                        <div>
                          <br></br>
                        </div>

                        <span>Password Settings</span>
                      </div>

                      <div className="sj-password-section">
                        <p className="sj-section-desc">
                          Leave blank if you don't want to change your password.
                        </p>

                        <Form.Group className="sj-form-group">
                          <Form.Label className="sj-form-label">Current Password</Form.Label>
                          <Form.Control
                            type="password"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter current password"
                            className="sj-form-control"
                          />
                        </Form.Group>

                        <Row>
                          <Col md={6}>
                            <Form.Group className="sj-form-group">
                              <Form.Label className="sj-form-label">New Password</Form.Label>
                              <Form.Control
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password"
                                className="sj-form-control"
                              />
                            </Form.Group>
                          </Col>

                          <Col md={6}>
                            <Form.Group className="sj-form-group">
                              <Form.Label className="sj-form-label">Confirm New Password</Form.Label>
                              <Form.Control
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                className="sj-form-control"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </div>

                      <div className="sj-settings-actions">
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading}
                          className="sj-save-btn"
                        >
                          {loading ? (
                            <>
                              <i className="fas fa-spinner fa-spin sj-btn-icon"></i>
                              Updating...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-user-edit sj-btn-icon"></i>
                              Update Profile
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* Notification Settings Tab */}
              <Tab.Pane eventKey="notifications">
                <Card className="sj-settings-card">
                  <Card.Header className="sj-card-header">
                    <div className="sj-header-content">
                      <i className="fas fa-bell sj-header-icon"></i>
                      <div>
                        <h5 className="sj-card-title">Notification Settings</h5>
                        <p className="sj-card-subtitle">Control how and when you receive notifications</p>
                      </div>
                    </div>
                  </Card.Header>

                  <Card.Body className="sj-card-body">
                    <Form onSubmit={saveNotificationSettings} className="sj-settings-form">
                      <div className="sj-notifications-list">
                        <div className="sj-notification-item">
                          <div className="sj-notification-content">
                            <div className="sj-notification-title">Email Notifications</div>
                            <div className="sj-notification-desc">
                              Receive important updates and alerts via email
                            </div>
                          </div>
                          <Form.Check
                            type="switch"
                            checked={emailNotifications}
                            onChange={(e) => setEmailNotifications(e.target.checked)}
                            className="sj-notification-switch"
                          />
                        </div>

                        <div className="sj-notification-item">
                          <div className="sj-notification-content">
                            <div className="sj-notification-title">New Post Notifications</div>
                            <div className="sj-notification-desc">
                              Get notified when new posts are published on your site
                            </div>
                          </div>
                          <Form.Check
                            type="switch"
                            checked={postNotifications}
                            onChange={(e) => setPostNotifications(e.target.checked)}
                            className="sj-notification-switch"
                          />
                        </div>

                        <div className="sj-notification-item">
                          <div className="sj-notification-content">
                            <div className="sj-notification-title">Comment Notifications</div>
                            <div className="sj-notification-desc">
                              Receive notifications when users comment on your posts
                            </div>
                          </div>
                          <Form.Check
                            type="switch"
                            checked={commentNotifications}
                            onChange={(e) => setCommentNotifications(e.target.checked)}
                            className="sj-notification-switch"
                          />
                        </div>

                        <div className="sj-notification-item">
                          <div className="sj-notification-content">
                            <div className="sj-notification-title">Newsletter</div>
                            <div className="sj-notification-desc">
                              Subscribe to our monthly newsletter with tips and updates
                            </div>
                          </div>
                          <Form.Check
                            type="switch"
                            checked={newsletter}
                            onChange={(e) => setNewsletter(e.target.checked)}
                            className="sj-notification-switch"
                          />
                        </div>
                      </div>

                      <div className="sj-settings-actions">
                        <Button
                          variant="primary"
                          type="submit"
                          disabled={loading}
                          className="sj-save-btn"
                        >
                          {loading ? (
                            <>
                              <i className="fas fa-spinner fa-spin sj-btn-icon"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-bell sj-btn-icon"></i>
                              Save Notification Settings
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
};

export default Settings;